import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeftRight,
  Clapperboard,
  ExternalLink,
  FileCheck,
  Github,
  Inbox,
  LayoutDashboard,
  LogOut,
  Menu,
  Package,
  Settings,
  ShoppingCart,
  Star,
  TriangleAlert,
  Users,
} from 'lucide-react';
import { USER_NAME } from '../configs/configs';
import { clearSession } from '../utils/session';
import { Drawer } from '../ui';
import { enquiriesAPI, ENQUIRIES_CHANGED_EVENT } from '../pages/admin/services/api';
import { useMediaQuery } from '../pages/admin/components/hooks';
import { getInitials } from '../pages/admin/utils/helpers';
import '../pages/admin/components/admin.css';

const NAV_GROUPS = [
  {
    label: 'Sales',
    items: [
      { to: '/admin/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/admin/orders', label: 'Orders', icon: ShoppingCart },
      { to: '/admin/transactions', label: 'Transactions', icon: ArrowLeftRight },
      { to: '/admin/delivery-issues', label: 'Delivery issues', icon: TriangleAlert },
      { to: '/admin/deliverables', label: 'Deliverables', icon: FileCheck },
    ],
  },
  {
    label: 'Inbox',
    items: [{ to: '/admin/enquiries', label: 'Enquiries', icon: Inbox, countKey: 'enquiries' }],
  },
  {
    label: 'Catalog',
    items: [
      { to: '/admin/products', label: 'Products', icon: Package },
      { to: '/admin/github-codes', label: 'Free code', icon: Github },
      { to: '/admin/videos', label: 'Videos', icon: Clapperboard },
      { to: '/admin/reviews', label: 'Reviews', icon: Star },
    ],
  },
  {
    label: 'Store',
    items: [
      { to: '/admin/users', label: 'Users', icon: Users },
      { to: '/admin/settings', label: 'Settings', icon: Settings },
    ],
  },
];

const ALL_ITEMS = NAV_GROUPS.flatMap(group => group.items);

/** The nav item for a path; nested pages like /admin/orders/:id belong to their parent. */
const activeItemFor = pathname => {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/admin') return ALL_ITEMS[0];
  return ALL_ITEMS.find(item => path === item.to || path.startsWith(`${item.to}/`)) || null;
};

const readUserName = () => {
  try {
    return localStorage.getItem(USER_NAME) || 'Admin';
  } catch {
    return 'Admin';
  }
};

const AdminNav = ({ activeTo, counts, onNavigate }) => (
  <>
    <nav className="adm-nav" aria-label="Admin">
      {NAV_GROUPS.map(group => (
        <React.Fragment key={group.label}>
          <div className="adm-nav__group">{group.label}</div>
          {group.items.map(({ to, label, icon: Icon, countKey }) => {
            const count = countKey ? counts[countKey] : 0;
            return (
              <Link
                key={to}
                to={to}
                className={`adm-nav__link${activeTo === to ? ' is-active' : ''}`}
                aria-current={activeTo === to ? 'page' : undefined}
                onClick={onNavigate}
              >
                <Icon aria-hidden="true" />
                <span>{label}</span>
                {count > 0 && (
                  <span className="adm-nav__count" aria-label={`${count} new`}>
                    {count > 99 ? '99+' : count}
                  </span>
                )}
              </Link>
            );
          })}
        </React.Fragment>
      ))}
    </nav>
    <div className="adm-side__foot">
      <Link
        to="/"
        className="adm-nav__link"
        target="_blank"
        rel="noopener noreferrer"
        onClick={onNavigate}
      >
        <ExternalLink aria-hidden="true" />
        <span>View site</span>
      </Link>
    </div>
  </>
);

const Brand = () => (
  <Link to="/admin/dashboard" className="adm-brand">
    <span className="adm-brand__mark" aria-hidden="true">
      PK
    </span>
    <span>Projects Kart</span>
    <span className="adm-brand__tag">Admin</span>
  </Link>
);

const AdminLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [menuOpen, setMenuOpen] = useState(false);
  const [counts, setCounts] = useState({ enquiries: 0 });
  const userName = useMemo(readUserName, []);

  const activeItem = activeItemFor(location.pathname);

  // The drawer is only for small screens; close it when the screen grows or the page changes.
  useEffect(() => {
    if (isDesktop) setMenuOpen(false);
  }, [isDesktop]);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const loadCounts = useCallback(async () => {
    try {
      const response = await enquiriesAPI.getAll({ status: 'new', itemsPerPage: 1 });
      const total = Number(response.data?.data?.totalCount) || 0;
      setCounts(prev => ({ ...prev, enquiries: total }));
    } catch {
      // The badge is optional; the inbox page shows its own errors.
    }
  }, []);

  useEffect(() => {
    loadCounts();
    window.addEventListener(ENQUIRIES_CHANGED_EVENT, loadCounts);
    return () => window.removeEventListener(ENQUIRIES_CHANGED_EVENT, loadCounts);
  }, [loadCounts]);

  const handleLogout = () => {
    clearSession();
    navigate('/');
  };

  return (
    <div className="adm-shell">
      <aside className="adm-side">
        <Brand />
        <AdminNav activeTo={activeItem?.to} counts={counts} />
      </aside>

      <Drawer
        open={menuOpen && !isDesktop}
        onClose={() => setMenuOpen(false)}
        placement="left"
        width={280}
        title="Menu"
        className="adm-nav-drawer"
      >
        <AdminNav activeTo={activeItem?.to} counts={counts} onNavigate={() => setMenuOpen(false)} />
      </Drawer>

      <div className="adm-main">
        <header className="adm-top">
          <button
            type="button"
            className="adm-icon-btn adm-menu-btn"
            aria-label="Open menu"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen(true)}
          >
            <Menu aria-hidden="true" />
          </button>
          <div className="adm-top__title">{activeItem?.label || 'Admin'}</div>
          <div className="adm-top__end">
            <div className="adm-user">
              <span className="adm-avatar" aria-hidden="true">
                {getInitials(userName) || 'A'}
              </span>
              <span className="adm-user__name">{userName}</span>
            </div>
            <button type="button" className="pk-btn pk-btn--secondary pk-btn--xs" onClick={handleLogout}>
              <LogOut aria-hidden="true" />
              Log out
            </button>
          </div>
        </header>

        <main className="adm-content">
          <div className="adm-page">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
